<template>
  <div class="card-panel flex flex-wrap gap-y-4">
    <div class="w-full">
      <div class="p-4">
        <div class="font-medium">
          {{ t('campaign_asset_set.asset_set') }}
        </div>
        <div class="w-full px-8 pt-4">
          <v-form
            ref="edmForm"
            v-slot="{ errors }"
            class="w-full lg:w-2/3"
            @submit.prevent=""
          >
            <div class="flex items-center justify-start gap-4">
              <text-field
                v-model="info.data.primarySubject"
                :name="t('campaign_asset_set.email_subject')"
                class="w-full py-2"
                rules="required"
                :error-messages="errors[t('campaign_asset_set.email_subject')]"
                :disabled="props.disabled"
              />
              <div class="mt-4 flex gap-2 lg:w-72">
                <switch-toggle
                  v-model="isTestAB"
                  name="testAB"
                  @change="onIsTestABChange"
                />
                <span class="text-sm">{{ t('campaign_asset_set.email_test_a_b') }}</span>
              </div>
            </div>
            <text-field
              v-if="isTestAB"
              v-model="info.data.secondarySubject"
              :name="t('campaign_asset_set.email_test_a_b')"
              class="py-2"
              rules="required"
              :error-messages="errors[t('campaign_asset_set.email_test_a_b')]"
              :disabled="props.disabled"
            />
          </v-form>
        </div>
        <div class="flex items-start px-8 py-4">
          <div class="w-fit pr-2 pt-2">
            {{ t('campaign_asset_set.is_apply_edm_template') }}：
          </div>
          <div class="flex flex-wrap items-center gap-y-4">
            <div class="flex w-full items-center">
              <radio
                v-model="isUseTemplate"
                name="check"
                value="yes"
                :disabled="props.disabled"
              >
                <template #text>
                  <div class="flex items-center gap-4">
                    <div>{{ t('yes') }}</div>
                    <selects
                      v-model="useTemplate"
                      :items="info.edmList"
                      :disabled="isUseTemplate !== 'yes' || !info.edmList?.length"
                      :placeholder="t('select_template')"
                      searchable
                      class="w-52"
                      @change="warnModal = true"
                    />
                  </div>
                </template>
              </radio>
            </div>
            <div class="'w-full flex items-center">
              <radio
                v-model="isUseTemplate"
                name="check"
                value="no"
                :text="t('no')"
                :disabled="props.disabled"
              />
            </div>
          </div>
        </div>
      </div>
      <hr />

      <div class="p-4">
        <div class="font-medium">
          {{ t('campaign_asset_set.insert_personal_variable') }}
        </div>
        <div class="flex px-8 py-4">
          <div class="w-1/12 pt-2">{{ t('campaign_asset_set.variable') }}：</div>
          <div class="w-11/12">
            <div class="mb-2 flex gap-4">
              <button
                v-for="(btn, btnIdx) in btnTemplate"
                :key="`btn-${btnIdx}`"
                class="btn disabled-btn bg-cdp-main-primary text-cdp-white hover:bg-cdp-main-primary-dark"
                :disabled="props.disabled"
                @click="copyText(btn.value)"
              >
                {{ btn.text }}
              </button>
            </div>
            <div class="flex items-center gap-1 text-xs">
              <icon name="ic_system_info" class="h-5 w-5" />
              {{ t('campaign_asset_set.copy_variable_code_to_paste_mail') }}
            </div>
          </div>
        </div>
      </div>
      <hr />

      <div class="flex flex-wrap gap-y-2 p-4">
        <div class="font-medium">
          {{ t('campaign.ai_recommend_keyword') }}
        </div>
        <div class="flex w-full gap-2 px-8 py-4">
          <div>{{ t('campaign.recommend_keyword') }}：</div>
          <div class="flex gap-2">
            <div
              v-for="(word, wordIdx) in props.keyword"
              :key="`word-${wordIdx}`"
              class="text-cdp-main-primary-dark"
            >
              {{ word.text }}
              <span v-show="wordIdx + 1 !== props.keyword?.length">、</span>
            </div>
            <span v-show="!props.keyword.length">
              {{ t('none') }}
            </span>
          </div>
        </div>
      </div>
      <hr />

      <div class="p-4">
        <div class="font-medium">
          {{ t('campaign_asset_set.context_editor') }}
          <span v-show="disabled"> (EDM {{ t('preview') }}) </span>
        </div>
      </div>
      <div class="p-4">
        <div class="relative">
          <beefree
            v-if="!disabled"
            :data="info.data.jsonfile"
            :client-id="info.clientId"
            :client-secret="info.clientSecret"
            class="h-[75vh]"
            @save="saveEdm"
            @sync-instance="syncInstance"
            @send="sendEdm"
            @error="edmError"
          />
          <div
            v-else
            v-dom-purify="info?.data?.htmlfile || ''"
            class="min-h-[30vh] rounded-md border p-4"
          ></div>
        </div>
      </div>
    </div>

    <modal v-if="warnModal" v-model="warnModal">
      <template #body>
        <div class="w-full p-4 lg:w-1/4">
          <div class="card-panel">
            <div class="relative p-6">
              <h3 class="mb-4 flex items-center gap-x-2 text-2xl">
                <icon name="ic_system_warning" class="h-6 w-6 text-cdp-yellow-400" />
                {{ t('campaign_asset_set.confirm_copy') }}
              </h3>
              <div class="text-left text-sm">
                {{ t('campaign_asset_set.conpy_warning') }}
              </div>
            </div>
            <div class="flex justify-end gap-3 p-5 pt-0">
              <button
                class="btn flex items-center border border-cdp-main-primary bg-cdp-main-primary-light text-cdp-main-primary hover:bg-cdp-primary-200"
                @click="warnModal = false"
              >
                {{ t('cancel') }}
              </button>
              <button
                class="btn bg-cdp-main-primary text-cdp-white hover:bg-cdp-main-primary-dark"
                @click="applyTemplate"
              >
                {{ t('copy') }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import {
  ApiEdmSave,
  ApiEdmList,
  ApiEdmProfile,
  ApiEdmBeefreeToken,
  ApiEdmApplyTemplate
} from '@/api/edm.js';

import clipBorad from 'vue-clipboard3';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';
import { permissionStore } from '@/stores/permission.js';

import toast from '@/plugins/sweetalert2.js';

import modal from '@/components/Modal.vue';
import radio from '@/components/Radio.vue';
import beefree from '@/components/Beefree.vue';

import { ApiEdmSendTestMail } from '@/api/edm';

import {
  toRaw,
  ref,
  reactive,
  computed,
  watch,
  onBeforeMount,
  defineComponent
} from 'vue';

import domPurifyDirective from '@/directives/v-dom-purify.js';

export default defineComponent({
  name: 'MarketingActivitiesEdmEdit',
  components: {
    modal,
    radio,
    beefree
  },

  directives: {
    domPurify: domPurifyDirective
  },
  props: {
    uuid: {
      type: String,
      default: ''
    },
    keyword: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['change', 'update:modelValue'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const storePermission = permissionStore();
    const { permission } = storeToRefs(storePermission);

    let edmForm = ref(null);
    const info = reactive({
      data: {
        name: '',
        htmlfile: null,
        jsonfile: {},
        abTestEnabled: false,
        primarySubject: '',
        secondarySubject: ''
      },
      edmList: [],
      clientId: null,
      clientSecret: null
    });

    const isTestAB = ref(false);
    const useTemplate = ref('');
    const isUseTemplate = ref('no');
    const warnModal = ref(false);

    const btnTemplate = [
      {
        text: t('campaign_asset_set.customer_full_name'),
        value: '{RECIPIENT_NAME}'
      },
      {
        text: t('campaign_asset_set.customer_email'),
        value: '{RECIPIENT_EMAIL}'
      }
    ];

    const currentPermission = computed(() => {
      return toRaw(permission.value)['campaign_asset'];
    });

    const permissionInit = watch(
      () => currentPermission,
      (permission) => {
        if (permission.value?.retrieve) {
          edmInit();
          permissionInit();
        }
      },
      { deep: true, immediate: false }
    );

    onBeforeMount(async () => {
      if (currentPermission.value?.retrieve) {
        await edmInit();
      }
    });

    async function edmInit() {
      const tokenData = await ApiEdmBeefreeToken();
      if (tokenData) {
        info.clientId = tokenData.clientId;
        info.clientSecret = tokenData.clientSecret;
      }

      const data = await ApiEdmList({});
      if (!data.msg) {
        info.edmList = data.results.map((item) => ({
          text: item.name,
          value: item.uuid
        }));
      }

      if (props.uuid) {
        await getInfo();
      }
    }

    async function getInfo() {
      const form = {
        uuid: props.uuid
      };
      const data = await ApiEdmProfile(form);
      if (!data.msg) {
        info.data = data;
        isTestAB.value = !!data.abTestEnabled;
      }
    }

    async function onIsTestABChange(val) {
      info.data.abTestEnabled = val;
    }

    const beefreeInstance = ref(null);
    function syncInstance(beefreeInst) {
      beefreeInstance.value = beefreeInst;
      emit('update:modelValue', beefreeInst);
    }

    async function sendEdm(htmlfile) {
      const requestBody = {
        subject: info.data.primarySubject,
        htmlfile: htmlfile
      };

      if (isTestAB.value) {
        requestBody.subject = info.data.secondarySubject;
      }

      await ApiEdmSendTestMail(requestBody);

      toast.fire({
        toast: true,
        icon: 'success',
        iconColor: 'green',
        text: t('send_success')
      });
    }

    async function saveEdm({ jsonfile, htmlfile }) {
      info.data.jsonfile = jsonfile;
      info.data.htmlfile = htmlfile;

      const form = {
        uuid: props.uuid,
        data: {
          ...info.data,
          abTestEnabled: isTestAB.value,
          jsonfile,
          htmlfile
        }
      };

      const data = await ApiEdmSave(form);
      if (!data.msg) {
        toast.fire({
          toast: true,
          icon: 'success',
          iconColor: 'green',
          text: 'EDM' + t('save_success')
        });
      }
      emit('change', form.data);
    }

    async function edmError() {
      toast.fire({
        toast: true,
        icon: 'error',
        iconColor: 'red',
        text: t('send_fail')
      });
    }

    async function copyText(text) {
      const clip = clipBorad;
      const { toClipboard } = clip();
      await toClipboard(text);
      toast.fire({
        toast: true,
        icon: 'success',
        iconColor: 'green',
        text: t('copy_success')
      });
    }

    async function applyTemplate() {
      const form = {
        uuid: props.uuid,
        templateUuid: useTemplate.value
      };
      const data = await ApiEdmApplyTemplate(form);
      if (!data.msg) {
        toast.fire({
          toast: true,
          icon: 'success',
          iconColor: 'green',
          text: t('copy_success')
        });
        await getInfo();
        warnModal.value = false;
      }
    }

    async function validate() {
      return await edmForm.value.validate();
    }

    return {
      t,
      props,
      edmForm,
      info,
      isTestAB,
      useTemplate,
      isUseTemplate,
      warnModal,
      btnTemplate,
      getInfo,
      sendEdm,
      edmError,
      saveEdm,
      syncInstance,
      copyText,
      applyTemplate,
      validate,
      onIsTestABChange
    };
  }
});
</script>